/**
 * Widget del asistente de la academia (B8 · §I).
 *
 * Lo usa gente **sin cuenta**, decidiendo si matricularse. Tres cosas lo
 * separan del tutor de contenido:
 *
 * 1. Cada respuesta enseña **sus fuentes**. Si no las hay, el bot no responde:
 *    escala. Hablar de precios con desconocidos sin grounding no es una opción.
 * 2. Cuando escala, ofrece **WhatsApp** y —si la persona deja un teléfono— crea
 *    el lead con la transcripción. El formulario de contacto solo aparece
 *    después de escalar: pedir datos antes de haber ayudado es lo que hace que
 *    la gente cierre el chat.
 * 3. **Solo informa** (decisión del dueño): no matricula, no cobra, no agenda.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { MessageCircle, Send, X } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { type BotCitation, useAskAcademyBot } from "./api";

interface Turn {
  role: "user" | "assistant";
  text: string;
  citations?: BotCitation[];
  whatsappUrl?: string | null;
  escalated?: boolean;
}

export const AcademyBotWidget: Component<{ orgSlug: string; academyName?: string }> = (props) => {
  const { t } = useI18n();
  const ask = useAskAcademyBot();
  const [open, setOpen] = createSignal(false);
  const [draft, setDraft] = createSignal("");
  const [turns, setTurns] = createSignal<Turn[]>([]);
  const [conversationId, setConversationId] = createSignal<string | undefined>();
  const [phone, setPhone] = createSignal("");
  const [name, setName] = createSignal("");
  const [leadSent, setLeadSent] = createSignal(false);

  const escalated = () => turns().some((turn) => turn.escalated);

  const send = async (contact?: { name: string; phone: string }) => {
    const message = draft().trim() || (contact ? t(msg`I would like you to contact me.`) : "");
    if (!message) {
      return;
    }
    setTurns((prev) => [...prev, { role: "user", text: message }]);
    setDraft("");
    try {
      const reply = await ask.mutateAsync({
        orgSlug: props.orgSlug,
        message,
        conversationId: conversationId(),
        contactName: contact?.name,
        contactPhone: contact?.phone,
      });
      setConversationId(reply.conversationId);
      if (reply.leadCreated) {
        setLeadSent(true);
      }
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          text: reply.answer,
          citations: reply.citations,
          whatsappUrl: reply.whatsappUrl,
          escalated: reply.escalated,
        },
      ]);
    } catch (error) {
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          text: error instanceof Error ? error.message : t(msg`Something went wrong.`),
          escalated: true,
        },
      ]);
    }
  };

  return (
    <>
      <Show when={!open()}>
        <button
          type="button"
          class="fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg"
          onClick={() => {
            setOpen(true);
          }}
          aria-label={t(msg`Ask the academy`)}
          data-testid="academy-bot-open"
        >
          <MessageCircle class="h-5 w-5" />
        </button>
      </Show>

      <Show when={open()}>
        <section
          class="fixed bottom-4 right-4 z-40 flex max-h-[70vh] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border/40 bg-background shadow-xl"
          data-testid="academy-bot-panel"
          aria-label={t(msg`Academy assistant`)}
        >
          <header class="flex items-center justify-between border-b border-border/40 px-4 py-3">
            <p class="text-sm font-semibold">{props.academyName ?? t(msg`Academy assistant`)}</p>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
              }}
              aria-label={t(msg`Close`)}
              data-testid="academy-bot-close"
            >
              <X class="h-4 w-4" />
            </button>
          </header>

          <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4" data-testid="academy-bot-turns">
            <Show when={turns().length === 0}>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Ask about prices, schedules, how to pay, requirements or certificates. I only give information — enrolling is done by a person.`,
                )}
              </p>
            </Show>
            <For each={turns()}>
              {(turn) => (
                <div
                  class="rounded-xl px-3 py-2 text-sm"
                  classList={{
                    "bg-primary/10 ml-6": turn.role === "user",
                    "bg-muted/50 mr-6": turn.role === "assistant",
                  }}
                  data-testid={`academy-bot-turn-${turn.role}`}
                >
                  <p>{turn.text}</p>
                  {/* Las fuentes son lo que hace verificable una respuesta sobre
                      precios. Sin ellas, el bot habría escalado. */}
                  <Show when={(turn.citations?.length ?? 0) > 0}>
                    <ul class="mt-2 space-y-0.5" data-testid="academy-bot-citations">
                      <For each={turn.citations}>
                        {(c) => <li class="text-xs text-muted-foreground">· {c.title}</li>}
                      </For>
                    </ul>
                  </Show>
                  <Show when={turn.whatsappUrl}>
                    {(url) => (
                      <a
                        class="mt-2 inline-flex rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground"
                        href={url()}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="academy-bot-whatsapp"
                      >
                        {t(msg`Talk to a person on WhatsApp`)}
                      </a>
                    )}
                  </Show>
                </div>
              )}
            </For>
          </div>

          {/* El formulario aparece SOLO tras escalar: pedir el teléfono antes de
              haber ayudado es lo que hace que la gente cierre el chat. */}
          <Show when={escalated() && !leadSent()}>
            <div class="space-y-2 border-t border-border/40 p-3" data-testid="academy-bot-contact">
              <p class="text-xs text-muted-foreground">
                {t(msg`Leave your name and phone and the academy will contact you.`)}
              </p>
              <input
                class="w-full rounded-lg border-2 border-border bg-field-background px-2 py-1.5 text-sm"
                placeholder={t(msg`Name`)}
                value={name()}
                onInput={(e) => {
                  setName(e.currentTarget.value);
                }}
                data-testid="academy-bot-name"
              />
              <div class="flex gap-2">
                <input
                  class="w-full rounded-lg border-2 border-border bg-field-background px-2 py-1.5 text-sm"
                  placeholder={t(msg`Phone`)}
                  value={phone()}
                  onInput={(e) => {
                    setPhone(e.currentTarget.value);
                  }}
                  data-testid="academy-bot-phone"
                />
                <Button
                  size="sm"
                  disabled={!phone().trim() || ask.isPending}
                  onClick={() => void send({ name: name(), phone: phone() })}
                  data-testid="academy-bot-send-contact"
                >
                  {t(msg`Send`)}
                </Button>
              </div>
            </div>
          </Show>
          <Show when={leadSent()}>
            <p
              class="border-t border-border/40 p-3 text-xs text-muted-foreground"
              data-testid="academy-bot-lead-sent"
            >
              {t(msg`Thanks. The academy has your details and will get in touch.`)}
            </p>
          </Show>

          <form
            class="flex gap-2 border-t border-border/40 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <input
              class="w-full rounded-lg border-2 border-border bg-field-background px-2 py-1.5 text-sm"
              placeholder={t(msg`Type your question…`)}
              value={draft()}
              onInput={(e) => {
                setDraft(e.currentTarget.value);
              }}
              data-testid="academy-bot-input"
            />
            <Button size="sm" type="submit" disabled={!draft().trim() || ask.isPending}>
              <Send class="h-4 w-4" />
            </Button>
          </form>
        </section>
      </Show>
    </>
  );
};
